import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Smile } from "lucide-react";
import { devError } from "@/lib/logger";
import { gameService } from "@/services";
import { REACTIONS } from "@/game/constants";

const COOLDOWN_MS = 2500;

/** Reaction bar + floating emoji overlay (CSS-only animations).
 * Props:
 *  - gameId, playerIdx (sender), socket (Socket.io client, optional — online)
 *  - isMuted (optional `(idx) => bool`) — drop incoming reactions from
 *    muted senders. Locally-applied so muting only affects what THIS
 *    client sees; the backend still broadcasts to everyone.
 */
export default function ReactionBar({ gameId, playerIdx, socket, isMuted }) {
  const { t } = useTranslation();
  const [flying, setFlying] = useState([]);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [open, setOpen] = useState(false);
  const flyingIdRef = useRef(0);
  const wrapperRef = useRef(null);
  // Stash the current `isMuted` predicate in a ref so the socket handler
  // closure always sees the latest mute set without re-subscribing on
  // every re-render.
  const isMutedRef = useRef(isMuted);
  useEffect(() => { isMutedRef.current = isMuted; }, [isMuted]);

  useEffect(() => {
    if (!socket) return;
    const handler = (payload) => {
      const senderIdx = payload?.player_idx;
      if (typeof senderIdx === "number" && isMutedRef.current?.(senderIdx)) return;
      pushFlying(payload.emoji, senderIdx);
    };
    socket.on("game_reaction", handler);
    return () => { socket.off("game_reaction", handler); };
  }, [socket]);

  const pushFlying = (emoji, fromIdx) => {
    const id = ++flyingIdRef.current;
    const startX = 60 + ((fromIdx || 0) * 22) + Math.random() * 10;
    const driftX = (Math.random() - 0.5) * 120;
    const rotate = (Math.random() - 0.5) * 30;
    setFlying((prev) => [...prev, { id, emoji, startX, driftX, rotate }]);
    setTimeout(() => setFlying((prev) => prev.filter((f) => f.id !== id)), 1600);
  };

  const sendReaction = async (emoji) => {
    const now = Date.now();
    if (now < cooldownUntil) return;
    setCooldownUntil(now + COOLDOWN_MS);
    pushFlying(emoji, playerIdx);
    setOpen(false); // collapse back to the single trigger after sending
    if (!gameId) return;
    try {
      await gameService.sendReaction(gameId, playerIdx, emoji);
    } catch (err) {
      devError("Failed to send reaction:", err);
    }
  };

  const cooldownActive = Date.now() < cooldownUntil;

  // Click-outside + Escape to dismiss the open palette so it doesn't
  // sit on top of the game board after the user changes their mind.
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <style>{`
        @keyframes reactionFloat {
          0%   { opacity: 0; transform: translate(0, 0) scale(0.5); }
          15%  { opacity: 1; transform: translate(calc(var(--drift) * 0.3), -60px) scale(1.6); }
          70%  { opacity: 1; transform: translate(calc(var(--drift) * 0.85), -200px) scale(1.2) rotate(var(--rot)); }
          100% { opacity: 0; transform: translate(var(--drift), -260px) scale(1) rotate(var(--rot)); }
        }
        .reaction-float { animation: reactionFloat 1.5s ease-out forwards; }
      `}</style>

      {/* Reaction bar — collapsed to a single trigger; clicking it
          pops the emoji palette above. Sending an emoji or clicking
          outside collapses it back. */}
      <div
        ref={wrapperRef}
        className="fixed bottom-4 right-4 z-40 flex flex-col items-end gap-2"
        data-testid="reaction-bar"
      >
        {open && (
          <div
            className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border border-white/40 shadow-lg rounded-full px-2 py-1.5 flex items-center gap-1"
            role="toolbar"
            aria-label={t("reactionBar.label.title")}
            data-testid="reaction-palette"
          >
            {REACTIONS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => sendReaction(emoji)}
                disabled={cooldownActive}
                data-testid={`reaction-btn-${emoji}`}
                className={`text-xl w-9 h-9 rounded-full hover:bg-[#38B6FF]/10 active:scale-90 transition-transform ${
                  cooldownActive ? "opacity-40 cursor-not-allowed" : ""
                }`}
                title={cooldownActive ? t("reactionBar.label.tooltipCooldown") : t("reactionBar.label.react", { emoji })}
                aria-label={cooldownActive ? t("reactionBar.label.cooldown") : t("reactionBar.label.send", { emoji })}
              >
                <span aria-hidden="true">{emoji}</span>
              </button>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="reaction-palette"
          aria-label={open ? t("reactionBar.label.close") : t("reactionBar.label.open")}
          data-testid="reaction-trigger-btn"
          className="w-11 h-11 rounded-full bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border border-white/40 shadow-lg hover:bg-[#38B6FF]/10 active:scale-90 transition-transform flex items-center justify-center text-[#1A237E] dark:text-[#E3F2FD]"
        >
          <Smile className="w-5 h-5" />
        </button>
      </div>

      {/* Floating emoji overlay — purely decorative (cosmetic
          animation), so `aria-hidden` so screen readers don't
          announce every emoji rain. The actual reactions are
          announced via the `reaction-bar` toolbar above. */}
      <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden" aria-hidden="true">
        {flying.map((f) => (
          <div
            key={f.id}
            className="absolute text-4xl reaction-float"
            style={{
              left: `${f.startX}%`,
              bottom: "10%",
              "--drift": `${f.driftX}px`,
              "--rot": `${f.rotate}deg`,
              filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.3))",
            }}
          >
            {f.emoji}
          </div>
        ))}
      </div>
    </>
  );
}
